import type { Router } from 'vue-router'
import { useToast } from 'vuestic-ui'

// index.html is rendered per request, so a reload always picks up the current
// manifest — a dynamic import that cannot be fetched means this tab is running
// a build whose hashed chunks are no longer on the server.
const CHUNK_LOAD_ERROR = /dynamically imported module|Importing a module script failed/i

const MARKER = 'chunk-reload'
// Long enough to cover the reload it guards, short enough that a failure on the
// same route an hour later still gets one.
const MARKER_TTL_MS = 10000

type Options = {
  /** The 'a new version is available' prompt, read when it is shown. */
  message: () => string
  /** Whether the page may be taken away right now. */
  canReload?: () => boolean
}

const isChunkLoadError = (error: unknown) =>
  CHUNK_LOAD_ERROR.test((error as Error | undefined)?.message ?? '')

// Reloading helps exactly once. When the same route fails again straight after,
// the chunk is missing for another reason — a broken deploy, no connection —
// and reloading again would spin.
const reloadedJustNow = (path: string) => {
  try {
    const [markedPath, at] = (sessionStorage.getItem(MARKER) ?? '').split(' ')
    return markedPath === path && Date.now() - Number(at) < MARKER_TTL_MS
  } catch {
    return false
  }
}

const markReload = (path: string) => {
  try {
    sessionStorage.setItem(MARKER, `${path} ${Date.now()}`)
  } catch {
    // Storage denied: the loop guard is best effort.
  }
}

export function installChunkReloadHandler(router: Router, { message, canReload }: Options) {
  let prompted = false
  const prompt = () => {
    if (prompted) return
    prompted = true
    useToast().notify({
      message: message(),
      color: 'info',
      duration: 0,
      closeable: true,
      onClick: () => window.location.reload(),
    })
  }

  let navigating = false
  router.beforeEach(() => { navigating = true })
  router.afterEach(() => { navigating = false })

  router.onError((error, to) => {
    navigating = false
    if (!isChunkLoadError(error)) return
    if (canReload?.() === false || reloadedJustNow(to.fullPath)) {
      prompt()
      return
    }
    markReload(to.fullPath)
    window.location.assign(to.fullPath)
  })

  // Imports outside a navigation — a lazy component in the page that is already
  // open. Reloading would throw away what the visitor was in the middle of, so
  // hand them the choice instead. The error still propagates, so the component
  // shows its own error state.
  window.addEventListener('vite:preloadError', () => {
    if (!navigating) prompt()
  })
}
